import { CadType, ObjectItemType } from 'webstudio/src/client/project_content_types';
import { EScannerKind } from '_data/types/Object3d';
import {
  ICadConeWithExtraUiFields,
  ICadCylinderWithExtraUiFields,
  ICadFreeformWithExtraUiFields,
  ICadItemWithUiFields,
  ICadModelWithExtraUiFields,
  ICadPlaneWithExtraUiFields,
  ICadSphereWithExtraUiFields,
  ICadTorusWithExtraUiFields,
  ICadTruncatedConeWithExtraUiFields,
  IGroupItemWithUiFields,
  IWSStubObject,
  IPhotoSetItemWithUiFields,
  IWSModelItemWithUiFields,
  IWSObjectItemWithUiFields,
  IWSPointCloudScanItemWithUiFields,
  IWSSimpleScanItemWithUiFields,
} from '_data/types/wsTypes';
import { isNonNullable } from '_helpers/nullable';

const isObject = (object: any): object is IWSObjectItemWithUiFields =>
  isNonNullable(object.type) &&
  [
    ObjectItemType.Scan,
    ObjectItemType.Model,
    ObjectItemType.Group,
    ObjectItemType.PhotoSet,
    ObjectItemType.Cad,
    ObjectItemType.PointCloud,
  ].includes(object.type);

export const isScanKind = (kind: ObjectItemType): boolean =>
  kind === ObjectItemType.Scan || kind === ObjectItemType.PointCloud;
export const isScanMicro = (scannerKind: EScannerKind): boolean => scannerKind === EScannerKind.Micro;
export const isScanRay = (scannerKind: EScannerKind, objectKind: ObjectItemType): boolean =>
  scannerKind === EScannerKind.Ray || scannerKind === EScannerKind.Ray2 || objectKind === ObjectItemType.PointCloud;
export const isScanEva = (scannerKind: EScannerKind, objectKind: ObjectItemType): boolean =>
  scannerKind === EScannerKind.Eva && objectKind === ObjectItemType.Scan;

// type guards
export const isScanObject = (
  object: IWSObjectItemWithUiFields
): object is IWSSimpleScanItemWithUiFields | IWSPointCloudScanItemWithUiFields =>
  object.type === ObjectItemType.Scan || object.type === ObjectItemType.PointCloud;
export const isGroupObject = (object: IWSObjectItemWithUiFields): object is IGroupItemWithUiFields =>
  object.type === ObjectItemType.Group;
export const isCadObject = (object: IWSObjectItemWithUiFields): object is ICadItemWithUiFields =>
  object.type === ObjectItemType.Cad;
export const isFusionObject = (object: IWSObjectItemWithUiFields): object is IWSModelItemWithUiFields =>
  object.type === ObjectItemType.Model;
export const isPhotoSetObject = (object: IWSObjectItemWithUiFields): object is IPhotoSetItemWithUiFields =>
  object.type === ObjectItemType.PhotoSet;

export const isCadModel = (object: IWSObjectItemWithUiFields): object is ICadModelWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Model;
export const isCylinderPrimitive = (object: IWSObjectItemWithUiFields): object is ICadCylinderWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Cylinder;
export const isConePrimitive = (object: IWSObjectItemWithUiFields): object is ICadConeWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Cone;
export const isTruncatedConePrimitive = (
  object: IWSObjectItemWithUiFields
): object is ICadTruncatedConeWithExtraUiFields => isCadObject(object) && object.cadType === CadType.TruncatedCone;
export const isSpherePrimitive = (object: IWSObjectItemWithUiFields): object is ICadSphereWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Sphere;
export const isPlanePrimitive = (object: IWSObjectItemWithUiFields): object is ICadPlaneWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Plane;
export const isTorusPrimitive = (object: IWSObjectItemWithUiFields): object is ICadTorusWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Torus;
export const isFreeformPrimitive = (object: IWSObjectItemWithUiFields): object is ICadFreeformWithExtraUiFields =>
  isCadObject(object) && object.cadType === CadType.Freeform;
export const isStubItem = (object: IWSObjectItemWithUiFields): object is IWSStubObject =>
  'isStub' in object && object.isStub;

interface IEditorUsageFlags {
  smoothingAllowed: boolean;
  erasingAllowed: boolean;
  mirroringAllowed: boolean;
  positioningAllowed: boolean;
  transformingAllowed: boolean;
}

const defaultEditorFlags: IEditorUsageFlags = {
  smoothingAllowed: true,
  erasingAllowed: true,
  mirroringAllowed: true,
  positioningAllowed: true,
  transformingAllowed: true,
};

export const computeEditorUsageFlagsForObject = (item: IWSObjectItemWithUiFields): IEditorUsageFlags => {
  if (isObject(item)) {
    if (isScanObject(item)) {
      if (item.scanner === EScannerKind.Ray) {
        return {
          ...defaultEditorFlags,
          smoothingAllowed: false,
          erasingAllowed: false,
        };
      }

      if (
        item.scanner &&
        [EScannerKind.Micro, EScannerKind.Eva, EScannerKind.Spider, EScannerKind.Leo].includes(item.scanner)
      ) {
        return {
          ...defaultEditorFlags,
          smoothingAllowed: false,
        };
      }
    }

    if (isFusionObject(item)) {
      return defaultEditorFlags;
    }

    if (isCadObject(item)) {
      return {
        ...defaultEditorFlags,
        smoothingAllowed: false,
        erasingAllowed: false,
      };
    }
  }

  return {
    smoothingAllowed: false,
    erasingAllowed: false,
    mirroringAllowed: false,
    positioningAllowed: false,
    transformingAllowed: false,
  };
};

export const computeEditorUsageFlagsForObjectsList = (
  arrayOfObjects: Array<IWSObjectItemWithUiFields>
): IEditorUsageFlags =>
  arrayOfObjects.reduce((result, object) => {
    const flagsObjectEntries: [string, boolean][] = Object.entries(computeEditorUsageFlagsForObject(object));

    return flagsObjectEntries.reduce((newResult, [flagKey, flagValue]) => {
      // eslint-disable-next-line no-param-reassign
      newResult[flagKey] = !newResult[flagKey] ? newResult[flagKey] : flagValue;
      return newResult;
    }, result);
  }, defaultEditorFlags);

export const canUseObjectForAlignment = (object: IWSObjectItemWithUiFields): boolean => {
  if (isObject(object)) {
    if (
      (isCadObject(object) && !isCadModel(object)) ||
      (isScanObject(object) &&
        object.scanner &&
        (isScanRay(object.scanner, object.type) || isScanMicro(object.scanner))) ||
      isPhotoSetObject(object)
    ) {
      return false;
    }

    return true;
  }

  return false;
};

export const canUseScanForPreparingAndModelMakingAndAutopilot = (object: IWSObjectItemWithUiFields): boolean =>
  isScanObject(object) &&
  ((object.scanner && !isScanRay(object.scanner, object.type) && !isScanMicro(object.scanner)) || !object.scanner);

export const getNameTextForDisabledObjectTooltip = (
  type: ObjectItemType,
  cadType: CadType,
  scannerType: EScannerKind
): string | null => {
  if (type === ObjectItemType.Cad) {
    return cadType === CadType.Model ? "CAD's" : 'Primitives';
  }

  if (isScanKind(type)) {
    if (isScanMicro(scannerType)) {
      return 'Micro scans';
    }
    if (isScanRay(scannerType, type)) {
      return 'Ray scans';
    }
  }

  if (type === ObjectItemType.PhotoSet) {
    return 'Photos';
  }

  return null;
};

export type RestrictedUsageFlagsKeys = keyof IEditorUsageFlags;
